import React, { useEffect, useMemo } from 'react';
import * as THREE from 'three';
import { Sky } from '@react-three/drei';
import { loadPbrMaps, disposePbrMaps, PBR_SETS } from './pbrTextures.jsx';
import { nearestPointOnPolyline } from './osmSite.jsx';
import asphaltColor from '../../assets/textures/asphalt/color.jpg';
import asphaltNormal from '../../assets/textures/asphalt/normal.jpg';
import asphaltRoughness from '../../assets/textures/asphalt/roughness.jpg';
import asphaltAo from '../../assets/textures/asphalt/ao.jpg';

// «سایت»: فقط آسمان + خیابان‌های واقعیِ اطرافِ ملک (از OpenStreetMap، osmSite.jsx) در ارتفاعِ واقعیِ طبقه —
// تا از پنجره‌ها در حالتِ اول‌شخص، پایین بودنِ خیابان و ارتفاعِ طبقه حس شود. ساختمان‌های اطراف عمداً ساخته
// نمی‌شوند (خواسته‌ی کاربر): حجم‌های ساختگیِ ساختمان «کارتونی» دیده می‌شدند.
//
// چیدمان (computeSiteLayout):
//   • جهتِ ملک (شمالی/جنوبی/شرقی/غربی) تعیین می‌کند خیابان پشتِ کدام ضلعِ پلان است (بالای پلان = شمال).
//   • از بینِ خیابان‌های نزدیک، خیابانی که در همان جهت است انتخاب می‌شود و کلِ داده طوری می‌چرخد که آن
//     خیابان دقیقاً روبه‌روی همان ضلعِ پلان و موازیِ آن باشد (پلان همیشه صاف کشیده می‌شود، خیابانِ واقعی نه).
//   • ضلعِ خیابانیِ پلان روی بَرِ ساختمان (لبه‌ی پیاده‌رو) می‌نشیند: فاصله تا محورِ خیابان = نصفِ عرضِ خیابان
//     + پیاده‌رو.
//   • زمین (خیابان) به‌اندازه‌ی «طبقه × ۳٫۲ متر» پایین‌تر از کفِ واحد است.
//
// کارایی (موبایل): همه‌ی خیابان‌ها یک مش، همه‌ی جدول‌ها یک مش، همه‌ی خط‌کشی‌ها یک مش — کلِ منظره چند draw call.

export const FLOOR_HEIGHT_CM = 320;
const GROUND_FLOOR_RAISE_CM = 40; // کفِ همکف معمولاً کمی بالاتر از پیاده‌رو است
const SIDEWALK_CM = 300;
const ROAD_PICK_RANGE_CM = 8000;
const GROUND_SIZE_CM = 120000;
const CURB_WIDTH_CM = 22; // جدولِ کنارِ خیابان
const LANE_LINE_WIDTH_CM = 12; // خطِ وسطِ خیابان (خط‌چین)
const LANE_DASH_CM = 300;
const LANE_GAP_CM = 500;
const LANE_MIN_ROAD_CM = 700; // خیابان‌های باریک‌تر (کوچه‌ها) خط‌کشی ندارند
const OUTDOOR_USERDATA = { khNoRoomLight: true }; // اسلایدرِ روشناییِ اتاق روی بیرون اثر ندارد

const ASPHALT_SET = { color: asphaltColor, normal: asphaltNormal, roughness: asphaltRoughness, ao: asphaltAo, baseSizeCm: 250 };

const ORIENTATION_NORMAL = {
  // جهتی که خیابان نسبت به ساختمان دارد (در پلان: +x شرق، +z جنوب)
  north: [0, 1], // ملکِ شمالی → خیابان در جنوب
  south: [0, -1],
  east: [-1, 0], // ملکِ شرقی → خیابان در غرب
  west: [1, 0],
};

// ---------------------------------------------------------------------------------------------------
// چیدمان

export function computeSiteLayout(site, context, planBounds) {
  const n = ORIENTATION_NORMAL[site?.orientation] || ORIENTATION_NORMAL.north;
  const roads = context?.roads || [];

  // خیابانِ جلوی ملک: نزدیک و در جهتِ درست (امتیاز = فاصله × جریمه‌ی اختلافِ جهت)
  let best = null;
  roads.forEach((road) => {
    const p = nearestPointOnPolyline(road.points, 0, 0);
    if (!p || p.dist > ROAD_PICK_RANGE_CM || p.dist < 1) return;
    const cos = (p.x * n[0] + p.z * n[1]) / p.dist;
    const score = p.dist * (2.2 - cos);
    if (!best || score < best.score) best = { road, p, score };
  });

  // چرخش: جهتِ «ملک → خیابان» در داده، روی جهتِ خیابانیِ پلان می‌افتد
  const theta = best ? Math.atan2(n[1], n[0]) - Math.atan2(best.p.z, best.p.x) : 0;
  const cos = Math.cos(theta);
  const sin = Math.sin(theta);
  const roadDist = best ? best.p.dist : 1200;
  const halfRoad = best ? best.road.widthCm / 2 : 500;

  // ضلعِ خیابانیِ پلان و مرکزِ پلان
  const corners = [
    [planBounds.minX, planBounds.minZ],
    [planBounds.maxX, planBounds.minZ],
    [planBounds.minX, planBounds.maxZ],
    [planBounds.maxX, planBounds.maxZ],
  ];
  const edge = Math.max(...corners.map(([x, z]) => x * n[0] + z * n[1]));
  const cx = (planBounds.minX + planBounds.maxX) / 2;
  const cz = (planBounds.minZ + planBounds.maxZ) / 2;
  const along = edge + halfRoad + SIDEWALK_CM - roadDist; // مختصاتِ لوکیشنِ ملک روی محورِ n
  const cn = cx * n[0] + cz * n[1];
  const pinX = cx + n[0] * (along - cn);
  const pinZ = cz + n[1] * (along - cn);

  const toPlan = ([x, z]) => [pinX + x * cos - z * sin, pinZ + x * sin + z * cos];

  // شمالِ واقعی در دستگاهِ پلان (برای جهتِ خورشید)
  // بردارِ (0, -1) (شمال در دادهِ OSM) بعد از همان چرخش
  const northPlan = [sin, -cos];

  const floor = Math.max(0, Number(site?.floor) || 0);
  const groundY = -(floor * FLOOR_HEIGHT_CM) - GROUND_FLOOR_RAISE_CM;

  const roadsPlan = roads.map((r) => ({ ...r, points: r.points.map(toPlan) }));

  return { groundY, pin: [pinX, pinZ], northPlan, roads: roadsPlan, frontRoadId: best?.road.id ?? null };
}

// ---------------------------------------------------------------------------------------------------
// هندسه

// نوارِ یک خیابان (مثلثی) با UVِ متناسب با اندازه‌ی واقعی: u در عرض، v در طول (سانتی‌متر / unit)
function buildRoadGeometry(roads, y, unitCm) {
  const pos = [];
  const uv = [];
  const idx = [];
  // خیابان‌های باریک‌تر اول (پایین‌تر)، پهن‌ترها رویشان — تقاطع‌ها z-fighting نکنند
  const sorted = [...roads].sort((a, b) => a.widthCm - b.widthCm);
  sorted.forEach((road, rank) => {
    const pts = road.points;
    if (pts.length < 2) return;
    const hw = road.widthCm / 2;
    const ry = y + rank * 0.6;
    let dist = 0;
    const base = pos.length / 3;
    for (let i = 0; i < pts.length; i += 1) {
      const prev = pts[Math.max(0, i - 1)];
      const next = pts[Math.min(pts.length - 1, i + 1)];
      let dx = next[0] - prev[0];
      let dz = next[1] - prev[1];
      const len = Math.hypot(dx, dz) || 1;
      dx /= len;
      dz /= len;
      if (i > 0) dist += Math.hypot(pts[i][0] - pts[i - 1][0], pts[i][1] - pts[i - 1][1]);
      const nx = -dz;
      const nz = dx;
      pos.push(pts[i][0] + nx * hw, ry, pts[i][1] + nz * hw, pts[i][0] - nx * hw, ry, pts[i][1] - nz * hw);
      uv.push(0, dist / unitCm, road.widthCm / unitCm, dist / unitCm);
      if (i > 0) {
        const a = base + (i - 1) * 2;
        idx.push(a, a + 2, a + 1, a + 1, a + 2, a + 3);
      }
    }
  });
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  g.setAttribute('uv', new THREE.Float32BufferAttribute(uv, 2));
  g.setIndex(idx);
  g.computeVertexNormals();
  // نرمال‌ها باید رو به بالا باشند (ترتیبِ رأس‌ها به جهتِ خیابان بستگی دارد)
  const normals = g.attributes.normal;
  for (let i = 0; i < normals.count; i += 1) normals.setXYZ(i, 0, 1, 0);
  g.setAttribute('uv2', g.attributes.uv);
  return g;
}


// نوارهای موازیِ یک خیابان (جدولِ دو طرف یا خط‌چینِ وسط): offsets = [از، تا] نسبت به محورِ خیابان
// (سانتی‌متر، مثبت = چپ)، dash = [طولِ خط، فاصله] یا null برای نوارِ پیوسته. همه در یک هندسه‌ی واحد
function buildStripGeometry(roads, y, pick) {
  const pos = [];
  const idx = [];
  const pushQuad = (a, b, c, d) => {
    const base = pos.length / 3;
    pos.push(...a, ...b, ...c, ...d);
    idx.push(base, base + 1, base + 2, base, base + 2, base + 3);
  };
  roads.forEach((road) => {
    const spec = pick(road);
    if (!spec) return;
    const pts = road.points;
    spec.strips.forEach(([o1, o2]) => {
      let phase = 0; // فاصله‌ی طی‌شده در الگوی خط‌چین
      for (let i = 0; i < pts.length - 1; i += 1) {
        const [x1, z1] = pts[i];
        const [x2, z2] = pts[i + 1];
        const len = Math.hypot(x2 - x1, z2 - z1);
        if (len < 1) continue;
        const dx = (x2 - x1) / len;
        const dz = (z2 - z1) / len;
        const nx = -dz;
        const nz = dx;
        const piece = (t0, t1) => {
          const ax = x1 + dx * t0;
          const az = z1 + dz * t0;
          const bx = x1 + dx * t1;
          const bz = z1 + dz * t1;
          pushQuad(
            [ax + nx * o1, y, az + nz * o1],
            [bx + nx * o1, y, bz + nz * o1],
            [bx + nx * o2, y, bz + nz * o2],
            [ax + nx * o2, y, az + nz * o2]
          );
        };
        if (!spec.dash) {
          piece(0, len);
          continue;
        }
        const [on, off] = spec.dash;
        const period = on + off;
        let t = 0;
        while (t < len) {
          const inPeriod = phase % period;
          if (inPeriod < on) {
            const end = Math.min(len, t + (on - inPeriod));
            piece(t, end);
            phase += end - t;
            t = end;
          } else {
            const end = Math.min(len, t + (period - inPeriod));
            phase += end - t;
            t = end;
          }
        }
      }
    });
  });
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  g.setIndex(idx);
  const normals = new Float32Array(pos.length);
  for (let i = 1; i < normals.length; i += 3) normals[i] = 1;
  g.setAttribute('normal', new THREE.BufferAttribute(normals, 3));
  return g;
}

// ---------------------------------------------------------------------------------------------------
// کامپوننت‌ها

function useSetMaps(set, key) {
  const maps = useMemo(() => loadPbrMaps(set, 1, 1), [key]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => () => disposePbrMaps(maps), [maps]);
  return maps;
}

function Roads({ roads, groundY }) {
  const maps = useSetMaps(ASPHALT_SET, 'asphalt');
  const geometry = useMemo(() => buildRoadGeometry(roads, groundY + 1.5, ASPHALT_SET.baseSizeCm), [roads, groundY]);
  useEffect(() => () => geometry.dispose(), [geometry]);
  return (
    <mesh geometry={geometry}>
      <meshStandardMaterial
        color="#ffffff"
        map={maps?.map || null}
        normalMap={maps?.normalMap || null}
        roughnessMap={maps?.roughnessMap || null}
        aoMap={maps?.aoMap || null}
        roughness={1}
        metalness={0}
        envMapIntensity={0.4}
        side={THREE.DoubleSide}
        userData={OUTDOOR_USERDATA}
      />
    </mesh>
  );
}

// جدولِ دو طرفِ خیابان (بتنِ روشن) و خط‌چینِ سفیدِ وسط — هرکدام یک مش برای همه‌ی خیابان‌ها
function RoadMarkings({ roads, groundY }) {
  const curbGeo = useMemo(
    () =>
      buildStripGeometry(roads, groundY + 2.5, (r) => {
        const hw = r.widthCm / 2;
        return { strips: [[hw - CURB_WIDTH_CM, hw], [-hw, -hw + CURB_WIDTH_CM]], dash: null };
      }),
    [roads, groundY]
  );
  const laneGeo = useMemo(
    () =>
      buildStripGeometry(roads, groundY + 2.6, (r) =>
        r.widthCm >= LANE_MIN_ROAD_CM && !r.oneway
          ? { strips: [[-LANE_LINE_WIDTH_CM / 2, LANE_LINE_WIDTH_CM / 2]], dash: [LANE_DASH_CM, LANE_GAP_CM] }
          : null
      ),
    [roads, groundY]
  );
  useEffect(
    () => () => {
      curbGeo.dispose();
      laneGeo.dispose();
    },
    [curbGeo, laneGeo]
  );
  return (
    <group>
      <mesh geometry={curbGeo}>
        <meshStandardMaterial color="#b9b5ad" roughness={0.9} metalness={0} side={THREE.DoubleSide} userData={OUTDOOR_USERDATA} />
      </mesh>
      <mesh geometry={laneGeo}>
        <meshStandardMaterial color="#e9e7e0" roughness={0.7} metalness={0} side={THREE.DoubleSide} userData={OUTDOOR_USERDATA} />
      </mesh>
    </group>
  );
}

// زمینِ اطراف (پیاده‌رو/حیاط‌ها) — فعلاً بتن/سیمانِ واقعی؛ با رسیدنِ عکسِ سنگ‌فرشِ پیاده‌رو عوض می‌شود
function Ground({ center, groundY }) {
  const set = PBR_SETS.concreteGray;
  const maps = useMemo(() => {
    const r = GROUND_SIZE_CM / set.baseSizeCm;
    return loadPbrMaps(set, r, r);
  }, [set]);
  useEffect(() => () => disposePbrMaps(maps), [maps]);
  return (
    <mesh position={[center[0], groundY, center[1]]} rotation={[-Math.PI / 2, 0, 0]}>
      <planeGeometry args={[GROUND_SIZE_CM, GROUND_SIZE_CM]} />
      <meshStandardMaterial
        color="#d9d5ce"
        map={maps?.map || null}
        normalMap={maps?.normalMap || null}
        roughnessMap={maps?.roughnessMap || null}
        roughness={1}
        metalness={0}
        envMapIntensity={0.4}
        userData={OUTDOOR_USERDATA}
      />
    </mesh>
  );
}

// داخلِ <group scale={M}> (واحد = سانتی‌متر) قرار می‌گیرد. layout: خروجیِ computeSiteLayout
export function SiteGeometry({ layout }) {
  return (
    <group>
      <Ground center={layout.pin} groundY={layout.groundY} />
      <Roads roads={layout.roads} groundY={layout.groundY} />
      <RoadMarkings roads={layout.roads} groundY={layout.groundY} />
    </group>
  );
}

// آسمانِ واقعی (شیدرِ پراکندگیِ جوِ three.js) — بیرون از group مقیاس‌دار (واحد = متر). خورشید از جنوبِ
// واقعی (بعد از چرخشِ داده)، کمی متمایل به غرب (بعدازظهر)، ۴۵ درجه بالای افق
export function SiteSky({ northPlan }) {
  const [nx, nz] = northPlan || [0, -1];
  const elev = (45 * Math.PI) / 180;
  const az = (20 * Math.PI) / 180; // از جنوب به سمتِ غرب
  // جنوب = -north؛ غرب = چرخشِ north به چپ
  const sx = -nx * Math.cos(az) + nz * Math.sin(az);
  const sz = -nz * Math.cos(az) - nx * Math.sin(az);
  const sun = [sx * Math.cos(elev) * 100, Math.sin(elev) * 100, sz * Math.cos(elev) * 100];
  return <Sky distance={900} sunPosition={sun} turbidity={5} rayleigh={1.1} mieCoefficient={0.004} mieDirectionalG={0.8} />;
}
